import { LANGUAGE_DISPLAY_NAMES } from '@shared/core/localized-defaults'
import classNames from 'classnames'
import { Globe } from 'lucide-react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { components, ControlProps, GroupHeadingProps, OptionProps, SingleValueProps } from 'react-select'
import { FormControl, SelectField } from 'src/modules/core/components'
import {
  AUTOMATIC_OPTION_VALUE,
  buildLanguageOptions,
  type MessageLanguageOption,
  type MessageLanguageOptionGroup,
  toChoice,
} from '../client-message-language-card.domain'
import type { MessageLanguageSelectProps } from './message-language-select.interface'
import classes from './message-language-select.module.scss'

function LanguageControl({ children, ...props }: ControlProps<MessageLanguageOption, false>) {
  return (
    <components.Control {...props}>
      <Globe className={classes.globe} size={20} aria-hidden="true" />
      {children}
    </components.Control>
  )
}

function LanguageOptionRow(props: OptionProps<MessageLanguageOption, false>) {
  const { label, secondary } = props.data
  return (
    <components.Option {...props}>
      <span className={classes.optionText}>
        <span className={classes.optionLabel}>{label}</span>
        {secondary && <span className={classes.optionSecondary}>{secondary}</span>}
      </span>
    </components.Option>
  )
}

function LanguageGroupHeading({ children }: GroupHeadingProps<MessageLanguageOption, false>) {
  return <div className={classes.groupHeading}>{children}</div>
}

function NoSeparator() {
  return null
}

function createTaggedSingleValue(tag: string) {
  return function TaggedSingleValue(props: SingleValueProps<MessageLanguageOption, false>) {
    return (
      <components.SingleValue {...props}>
        <span className={classes.singleValue}>
          {props.children}
          {props.data.value !== AUTOMATIC_OPTION_VALUE && <span className={classes.chosenTag}>{tag}</span>}
        </span>
      </components.SingleValue>
    )
  }
}

// SelectField drops the click that lands right after onMenuClose, and react-select also calls it on blur,
// so the first click on Save changes or Cancel after a pick was lost. Overriding it opts out of that.
function keepNextClick() {
  return undefined
}

const SELECT_COMPONENTS = {
  Control: LanguageControl,
  Option: LanguageOptionRow,
  GroupHeading: LanguageGroupHeading,
  IndicatorSeparator: NoSeparator,
}

export function MessageLanguageSelect({
  value,
  automaticLanguage,
  automaticSourceText,
  onChange,
  disabled,
  automaticLabel,
  groupHeading,
  chosenTag,
  fullWidth = false,
  look = 'card',
  className,
}: MessageLanguageSelectProps) {
  const { formatMessage } = useIntl()
  const label = formatMessage({ id: 'settings.messageLanguage.fieldLabel' })

  const { groups, flatOptions } = useMemo(() => {
    const automatic: MessageLanguageOption = {
      value: AUTOMATIC_OPTION_VALUE,
      label:
        automaticLabel ??
        formatMessage(
          { id: 'settings.messageLanguage.automatic' },
          { language: LANGUAGE_DISPLAY_NAMES[automaticLanguage].english },
        ),
      secondary: automaticSourceText,
    }
    const languages = buildLanguageOptions()
    const fixed: MessageLanguageOptionGroup = {
      label: groupHeading ?? formatMessage({ id: 'settings.messageLanguage.groupHeading' }),
      options: languages,
    }
    return { groups: [automatic, fixed], flatOptions: [automatic, ...languages] }
  }, [automaticLanguage, automaticSourceText, automaticLabel, groupHeading, formatMessage])

  const selectComponents = useMemo(
    () => (chosenTag ? { ...SELECT_COMPONENTS, SingleValue: createTaggedSingleValue(chosenTag) } : SELECT_COMPONENTS),
    [chosenTag],
  )

  const selected = flatOptions.find((option) => option.value === (value ?? AUTOMATIC_OPTION_VALUE))

  return (
    <FormControl
      label={label}
      labelClassName={look === 'card' ? classes.label : undefined}
      className={classNames(classes.field, className)}
    >
      <SelectField
        variant="small"
        className={classNames(classes.select, { [classes.fullWidth]: fullWidth, [classes.formLook]: look === 'form' })}
        classNamePrefix="message-language"
        aria-label={label}
        options={groups}
        value={selected}
        isDisabled={disabled}
        closeMenuOnSelect={true}
        onMenuClose={keepNextClick}
        components={selectComponents}
        onChange={(option: MessageLanguageOption | null) => onChange(toChoice(option))}
      />
    </FormControl>
  )
}
